import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Maximize2, X, Play, Pause } from 'lucide-react';

interface MediaItem {
    media_hash: string;
    type: string;
    order: number;
    media: {
        hash: string;
        filename: string;
        mime_type: string;
        type: string;
        path: string;
    };
}

interface GalleryProps {
    media: MediaItem[];
}

const Gallery: React.FC<GalleryProps> = ({ media }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isPlaying, setIsPlaying] = useState<boolean[]>(new Array(media.length).fill(false));
    const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
    const observerRefs = useRef<(IntersectionObserver | null)[]>([]);

    const cdnURL = 'https://walkies.nyc3.cdn.digitaloceanspaces.com/';

    useEffect(() => {
        videoRefs.current = videoRefs.current.slice(0, media.length);
        observerRefs.current = observerRefs.current.slice(0, media.length);

        media.forEach((item, index) => {
            if (item.media.mime_type.startsWith('video/')) {
                const options = {
                    root: null,
                    rootMargin: '0px',
                    threshold: 0.5,
                };

                const observer = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            if (isPlaying[index]) {
                                const video = entry.target as HTMLVideoElement;
                                video.play().catch(() => {
                                    setIsPlaying(prev => {
                                        const newState = [...prev];
                                        newState[index] = false;
                                        return newState;
                                    });
                                });
                            }
                        } else {
                            const video = entry.target as HTMLVideoElement;
                            video.pause();
                        }
                    });
                }, options);

                if (videoRefs.current[index]) {
                    observer.observe(videoRefs.current[index]!);
                }

                observerRefs.current[index] = observer;
            }
        });

        return () => {
            observerRefs.current.forEach((observer) => {
                if (observer) {
                    observer.disconnect();
                }
            });
        };
    }, [media, isPlaying]);

    const goToNextImage = () => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % media.length);
    };

    const goToPreviousImage = () => {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + media.length) % media.length);
    };

    const openFullScreen = () => {
        setIsFullScreen(true);
    };

    const closeFullScreen = () => {
        setIsFullScreen(false);
    };

    const togglePlay = (index: number) => {
        setIsPlaying(prev => {
            const newState = [...prev];
            newState[index] = !newState[index];
            return newState;
        });

        if (videoRefs.current[index]) {
            if (isPlaying[index]) {
                videoRefs.current[index]!.pause();
            } else {
                videoRefs.current[index]!.play().catch(() => {
                    setIsPlaying(prev => {
                        const newState = [...prev];
                        newState[index] = false;
                        return newState;
                    });
                });
            }
        }
    };

    const currentItem = media[currentIndex];

    return (
        <div className="w-full relative">
            <div className="w-full h-[30rem] overflow-hidden rounded-lg relative">
                {currentItem.media.mime_type.startsWith('image/') ? (
                    <img
                        src={`${cdnURL}${currentItem.media.path}`}
                        alt={currentItem.media.filename}
                        className="w-full h-full object-cover transition-all duration-300 ease-in-out"
                    />
                ) : (
                    <video
                        ref={(el) => (videoRefs.current[currentIndex] = el)}
                        src={`${cdnURL}${currentItem.media.path}`}
                        className="w-full h-full object-cover"
                        loop
                        muted
                        playsInline
                    />
                )}

                {/* Navigation arrows */}
                <button
                    onClick={goToPreviousImage}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-all duration-200"
                    aria-label="Previous item"
                >
                    <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                    onClick={goToNextImage}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-all duration-200"
                    aria-label="Next item"
                >
                    <ChevronRight className="w-6 h-6" />
                </button>

                {/* Play/Pause and Full-screen buttons for videos */}
                {currentItem.media.mime_type.startsWith('video/') && (
                    <>
                        <button
                            onClick={() => togglePlay(currentIndex)}
                            className="absolute bottom-4 left-4 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-all duration-200"
                            aria-label={isPlaying[currentIndex] ? "Pause" : "Play"}
                        >
                            {isPlaying[currentIndex] ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                        </button>
                        <button
                            onClick={openFullScreen}
                            className="absolute bottom-4 left-16 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-all duration-200"
                            aria-label="Full screen"
                        >
                            <Maximize2 className="w-6 h-6" />
                        </button>
                    </>
                )}
            </div>

            {/* Image/Video counter */}
            <div className="absolute bottom-4 right-4 bg-black bg-opacity-50 text-white px-2 py-1 rounded-full text-sm">
                {currentIndex + 1} / {media.length}
            </div>

            {/* Full-screen video modal */}
            {isFullScreen && currentItem.media.mime_type.startsWith('video/') && (
                <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
                    <video
                        src={`${cdnURL}${currentItem.media.path}`}
                        className="max-w-full max-h-full"
                        controls
                        autoPlay
                    />
                    <button
                        onClick={closeFullScreen}
                        className="absolute top-4 right-4 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-all duration-200"
                        aria-label="Close full screen"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default Gallery;